import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Body zone definitions with simplified SVG paths
// Body zone definitions with SVG paths for organic line-art style
const BODY_ZONES = [
    // ════════ FRONT VIEW ════════
    // Head & Neck
    {
        id: 'head',
        label: 'Head & Face',
        d: 'M 200,45 m -30,0 a 30,38 0 1,0 60,0 a 30,38 0 1,0 -60,0'
    },
    {
        id: 'throat',
        label: 'Throat & Neck',
        d: 'M 185,85 L 185,100 Q 200,105 215,100 L 215,85 Q 200,92 185,85 Z'
    },
    // Upper Arms & Shoulders
    {
        id: 'shoulder_left',
        label: 'Left Shoulder',
        d: 'M 185,100 Q 150,100 135,120 L 130,125 L 165,125 Z'
    },
    {
        id: 'shoulder_right',
        label: 'Right Shoulder',
        d: 'M 215,100 Q 250,100 265,120 L 270,125 L 235,125 Z'
    },
    {
        id: 'arm_upper_left',
        label: 'Left Arm (Upper)',
        d: 'M 130,125 L 120,180 L 145,180 L 150,130 Z'
    },
    {
        id: 'arm_upper_right',
        label: 'Right Arm (Upper)',
        d: 'M 270,125 L 280,180 L 255,180 L 250,130 Z'
    },
    // Forearms
    {
        id: 'arm_lower_left',
        label: 'Left Forearm',
        d: 'M 120,180 L 115,230 L 140,230 L 145,180 Z'
    },
    {
        id: 'arm_lower_right',
        label: 'Right Forearm',
        d: 'M 280,180 L 285,230 L 260,230 L 255,180 Z'
    },
    // Hands
    {
        id: 'hand_left',
        label: 'Left Hand',
        d: 'M 115,230 L 110,255 L 135,255 L 140,230 Z'
    },
    {
        id: 'hand_right',
        label: 'Right Hand',
        d: 'M 285,230 L 290,255 L 265,255 L 260,230 Z'
    },

    // Torso
    {
        id: 'chest',
        label: 'Chest & Heart Center',
        d: 'M 165,125 L 235,125 L 230,180 L 170,180 Z'
    },
    {
        id: 'stomach',
        label: 'Stomach / Solar Plexus',
        d: 'M 170,180 L 230,180 L 225,230 L 175,230 Z'
    },
    {
        id: 'abdomen',
        label: 'Lower Abdomen / Sacral',
        d: 'M 175,230 L 225,230 L 220,280 L 180,280 Z'
    },
    {
        id: 'hips',
        label: 'Hips / Pelvis',
        d: 'M 180,280 L 220,280 L 235,310 L 165,310 Z'
    },

    // Legs (Front Only)
    { // Thighs
        id: 'thigh_left',
        label: 'Left Thigh',
        d: 'M 165,310 L 185,310 L 180,420 L 150,420 Z'
    },
    {
        id: 'thigh_right',
        label: 'Right Thigh',
        d: 'M 215,310 L 235,310 L 250,420 L 220,420 Z'
    },
    {
        id: 'knee_left',
        label: 'Left Knee',
        d: 'M 150,420 L 180,420 L 178,450 L 152,450 Z'
    },
    {
        id: 'knee_right',
        label: 'Right Knee',
        d: 'M 220,420 L 250,420 L 248,450 L 222,450 Z'
    },
    {
        id: 'calf_left',
        label: 'Left Calf / Shin',
        d: 'M 152,450 L 178,450 L 175,550 L 155,550 Z'
    },
    {
        id: 'calf_right',
        label: 'Right Calf / Shin',
        d: 'M 222,450 L 248,450 L 245,550 L 225,550 Z'
    },
    {
        id: 'foot_left',
        label: 'Left Foot',
        d: 'M 155,550 L 175,550 L 180,570 L 145,570 Z'
    },
    {
        id: 'foot_right',
        label: 'Right Foot',
        d: 'M 225,550 L 245,550 L 255,570 L 220,570 Z'
    },

    // ════════ BACK VIEW (Approx +300 X) ════════
    // Back View Center approx 500
    // Torso only as requested (No legs, no shoulders)
    {
        id: 'back_shoulders',
        label: 'Upper back / Shoulders',
        d: 'M 460,100 L 540,100 L 535,130 L 465,130 Z'
    },
    {
        id: 'back_upper',
        label: 'Upper Back',
        d: 'M 465,130 L 535,130 L 530,200 L 470,200 Z'
    },
    {
        id: 'back_lower',
        label: 'Lower Back',
        d: 'M 470,200 L 530,200 L 525,270 L 475,270 Z'
    },
    {
        id: 'back_hips',
        label: 'Lower back / Hips',
        d: 'M 475,270 L 525,270 L 515,310 L 485,310 Z'
    }
];

const SENSATION_TYPES = [
    { id: 'pain', label: 'Pain', icon: '⚡', color: '#EF4444', description: 'Sharp or dull discomfort' },
    { id: 'stiffness', label: 'Stiffness', icon: '🔒', color: '#F59E0B', description: 'Reduced mobility or tightness' },
    { id: 'heaviness', label: 'Heaviness', icon: '⬇️', color: '#8B5CF6', description: 'Feeling weighed down' },
    { id: 'heat', label: 'Heat/Burning', icon: '🔥', color: '#DC2626', description: 'Warmth or burning sensation' },
    { id: 'cold', label: 'Coldness', icon: '❄️', color: '#3B82F6', description: 'Feeling cold or numb' },
    { id: 'tension', label: 'Tension', icon: '💢', color: '#EC4899', description: 'Tight or stressed feeling' },
];

// Teaching insights based on zone selection
const ZONE_INSIGHTS: Record<string, string> = {
    head: "Headaches often relate to stress, digestion, or sleep patterns in Ayurveda.",
    throat: "The throat is where we 'swallow' emotions—tension here may reflect unexpressed feelings.",
    chest: "The chest houses the heart chakra—tightness here often connects to emotional holding.",
    joints: "Joint issues in Ayurveda are linked to Vata dosha—especially when multiple joints are affected.",
    digestive: "Ayurveda calls digestion the 'root of health'—imbalances here affect the whole body.",
    feet: "The feet are connected to grounding (Vata). Cold or dry feet often indicate an air imbalance.",
    hands: "Hands relate to grasping and letting go. Tension here can indicate holding onto stress.",
};

interface BodyMapCanvasProps {
    onComplete: (data: Record<string, any>) => void | Promise<void>;
    hideEducationalLink?: boolean;
}

export default function BodyMapCanvas({ onComplete, hideEducationalLink }: BodyMapCanvasProps) {
    const [selectedZones, setSelectedZones] = useState<Map<string, any>>(new Map());
    const [activeZoneIds, setActiveZoneIds] = useState<Set<string>>(new Set());
    const [showInsight, setShowInsight] = useState<string | null>(null);

    const handleZoneClick = (zone: any) => {
        const newActive = new Set(activeZoneIds);
        let justAdded = false;

        if (newActive.has(zone.id)) {
            newActive.delete(zone.id);
        } else {
            newActive.add(zone.id);
            justAdded = true;
        }
        setActiveZoneIds(newActive);

        // Show teaching insight for the most recently clicked zone if it was just added
        if (justAdded) {
            setTimeout(() => {
                if (zone.id.includes('knee') || zone.id.includes('shoulder')) {
                    setShowInsight(ZONE_INSIGHTS.joints);
                } else if (['stomach', 'abdomen'].includes(zone.id)) {
                    setShowInsight(ZONE_INSIGHTS.digestive);
                } else if (zone.id.includes('foot')) {
                    setShowInsight(ZONE_INSIGHTS.feet);
                } else if (zone.id.includes('hand')) {
                    setShowInsight(ZONE_INSIGHTS.hands);
                } else {
                    setShowInsight(ZONE_INSIGHTS[zone.id]);
                }
            }, 500);
        } else {
            setShowInsight(null); // Clear insight if a zone is deselected
        }
    };

    const handleSensationSelect = (sensation: any) => {
        if (activeZoneIds.size > 0) {
            const newSelection = new Map(selectedZones);

            // Apply sensation to all active zones
            activeZoneIds.forEach(id => {
                const zoneDef = BODY_ZONES.find(z => z.id === id);
                if (zoneDef) {
                    newSelection.set(id, {
                        zone: zoneDef.label,
                        sensation: sensation.label,
                        color: sensation.color,
                    });
                }
            });

            setSelectedZones(newSelection);
            setActiveZoneIds(new Set()); // Clear selection after applying
            setShowInsight(null);
        }
    };

    const detectPattern = () => {
        const zones = Array.from(selectedZones.keys());
        const jointZones = zones.filter(z => z.includes('knee') || z.includes('shoulder'));
        const digestiveZones = zones.filter(z => ['stomach', 'abdomen'].includes(z));

        if (jointZones.length >= 2) {
            return {
                type: 'vata_imbalance',
                message: "Multiple joint areas affected suggests a Vata pattern—your body craves warmth and stability.",
            };
        }

        if (digestiveZones.length > 0 && zones.includes('back_lower')) {
            return {
                type: 'digestive_connection',
                message: "Digestive discomfort with back pain shows a deep connection—both are governed by the same energy centers.",
            };
        }

        if (['chest', 'throat', 'head'].every(z => zones.includes(z))) {
            return {
                type: 'stress_pathway',
                message: "These three areas form the 'stress pathway'—your body is showing signs of mental-emotional strain.",
            };
        }

        return null;
    };

    const pattern = selectedZones.size >= 2 ? detectPattern() : null;

    const handleContinue = () => {
        if (onComplete) {
            const bodyMapData = Object.fromEntries(selectedZones);
            onComplete(bodyMapData);
        }
    };

    // Helper to get active zone label for headline
    const getActiveZoneLabel = () => {
        if (activeZoneIds.size === 0) return '';
        if (activeZoneIds.size > 1) return ''; // Will use generic headline
        const id = Array.from(activeZoneIds)[0];
        return BODY_ZONES.find(z => z.id === id)?.label || '';
    };

    return (
        <div className="w-full max-w-4xl mx-auto p-4 md:p-6 bg-sage-50 min-h-screen font-sans">
            {/* Header */}
            <div className="text-center mb-8 pt-4">
                <h2 className="text-3xl font-serif font-bold text-sage-900 mb-2">
                    Where Do You Feel It? 🗺️
                </h2>
                <p className="text-sage-700">
                    Tap multiple areas to build your body map.
                </p>
                {!hideEducationalLink && (
                    <div className="mt-4">
                        <a href="/learn/what-is-ayurveda" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#52B788] hover:text-[#40916c] hover:underline flex items-center justify-center gap-1 transition-colors">
                            🌿 New to Ayurveda? Learn what it is and how it works →
                        </a>
                    </div>
                )}
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-stretch h-full">
                {/* Body Map SVG - Now using min-height to ensure it's tall enough */}
                <div className="flex-1 bg-white rounded-3xl p-6 shadow-sm border border-sage-100 w-full relative min-h-[600px] flex flex-col justify-center">
                    <svg viewBox="0 0 600 600" className="w-full h-auto max-h-[80vh]">
                        <defs>
                            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                        </defs>

                        {/* Front view body outline */}
                        <g id="front-body">
                            {BODY_ZONES.filter(z => !z.id.includes('back')).map((zone) => {
                                const isSelected = selectedZones.has(zone.id);
                                const isActive = activeZoneIds.has(zone.id);

                                // Organic Line Layout Styling
                                const strokeColor = isSelected ? selectedZones.get(zone.id).color : (isActive ? '#D4A373' : '#A3B18A');
                                const fillColor = isSelected ? `${selectedZones.get(zone.id).color}40` : (isActive ? '#D4A37320' : 'rgba(212, 163, 115, 0)');
                                const strokeWidth = isSelected || isActive ? 2.5 : 1.5;

                                return (
                                    <motion.path
                                        key={zone.id}
                                        d={zone.d}
                                        fill={fillColor}
                                        stroke={strokeColor}
                                        strokeWidth={strokeWidth}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="cursor-pointer transition-all"
                                        whileHover={{ scale: 1.02, strokeWidth: 3 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleZoneClick(zone)}
                                        initial={{ pathLength: 0, opacity: 0, fill: 'rgba(212, 163, 115, 0)' }}
                                        animate={{
                                            pathLength: 1,
                                            opacity: 1,
                                            fill: fillColor,
                                            stroke: strokeColor
                                        }}
                                        transition={{ duration: 0.8, ease: "easeInOut" }}
                                    />
                                );
                            })}
                        </g>

                        {/* Back view body outline */}
                        <g id="back-body">
                            {BODY_ZONES.filter(z => z.id.includes('back')).map((zone) => {
                                const isSelected = selectedZones.has(zone.id);
                                const isActive = activeZoneIds.has(zone.id);

                                const strokeColor = isSelected ? selectedZones.get(zone.id).color : (isActive ? '#D4A373' : '#A3B18A');
                                const fillColor = isSelected ? `${selectedZones.get(zone.id).color}40` : (isActive ? '#D4A37320' : 'rgba(212, 163, 115, 0)');
                                const strokeWidth = isSelected || isActive ? 2.5 : 1.5;

                                return (
                                    <motion.path
                                        key={zone.id}
                                        d={zone.d}
                                        fill={fillColor}
                                        stroke={strokeColor}
                                        strokeWidth={strokeWidth}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="cursor-pointer transition-all"
                                        whileHover={{ scale: 1.02, strokeWidth: 3 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleZoneClick(zone)}
                                        initial={{ pathLength: 0, opacity: 0, fill: 'rgba(212, 163, 115, 0)' }}
                                        animate={{
                                            pathLength: 1,
                                            opacity: 1,
                                            fill: fillColor,
                                            stroke: strokeColor
                                        }}
                                        transition={{ duration: 0.8, ease: "easeInOut", delay: 0.2 }}
                                    />
                                );
                            })}
                        </g>

                        {/* Labels - Moved to bottom as requested */}
                        <text x="200" y="590" fontSize="14" fontWeight="bold" fill="#76a076" textAnchor="middle" className="tracking-widest uppercase">
                            Front View
                        </text>
                        <text x="500" y="590" fontSize="14" fontWeight="bold" fill="#76a076" textAnchor="middle" className="tracking-widest uppercase">
                            Back View
                        </text>

                        {/* Fix 6 & 9: Back View Labels - Enlarged */}
                        <text x="440" y="118" fontSize="12" fontWeight="bold" fill="#A3B18A" textAnchor="end" className="pointer-events-none">
                            Upper back / Shoulders
                        </text>
                        <text x="440" y="165" fontSize="12" fontWeight="bold" fill="#A3B18A" textAnchor="end" className="pointer-events-none">
                            Upper back
                        </text>
                        <text x="440" y="235" fontSize="12" fontWeight="bold" fill="#A3B18A" textAnchor="end" className="pointer-events-none">
                            Lower back
                        </text>
                        <text x="440" y="295" fontSize="12" fontWeight="bold" fill="#A3B18A" textAnchor="end" className="pointer-events-none">
                            Lower back / Hips
                        </text>
                    </svg>

                    {/* Simple Legend */}
                    <div className="absolute top-4 right-4 flex flex-col gap-2 text-xs text-sage-600">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full border border-sage-400"></div>
                            <span>Tap to select</span>
                        </div>
                    </div>
                </div>

                {/* Right Panel: Sensation Selector & Insights */}
                <div className="flex-1 space-y-6 w-full">
                    {/* Active Zone Sensation Selector */}
                    <AnimatePresence>
                        {activeZoneIds.size > 0 && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="bg-white rounded-3xl shadow-lg p-6 border-2 border-earth-200"
                            >
                                <h3 className="text-lg font-serif font-semibold mb-4 text-sage-900">
                                    {activeZoneIds.size > 1 ? (
                                        <span>How do these areas feel?</span>
                                    ) : (
                                        <span>How does your <span className="text-earth-600 font-bold">{getActiveZoneLabel()}</span> feel?</span>
                                    )}
                                </h3>

                                <div className="grid grid-cols-2 gap-3">
                                    {SENSATION_TYPES.map((sensation) => (
                                        <motion.button
                                            key={sensation.id}
                                            onClick={() => handleSensationSelect(sensation)}
                                            className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 
                               hover:shadow-md transition-all text-center"
                                            style={{
                                                borderColor: sensation.color,
                                                backgroundColor: `${sensation.color}10`
                                            }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <span className="text-3xl">{sensation.icon}</span>
                                            <span className="font-medium text-sage-800 text-sm">
                                                {sensation.label}
                                            </span>
                                            <span className="text-xs text-sage-600 opacity-80">
                                                {sensation.description}
                                            </span>
                                        </motion.button>
                                    ))}
                                </div>

                                {/* Teaching Insight */}
                                {showInsight && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-4 p-4 bg-earth-50 rounded-2xl border-l-4 border-earth-500"
                                    >
                                        <p className="text-sm text-earth-900 leading-relaxed">
                                            <span className="font-bold">💡 Did you know?</span> {showInsight}
                                        </p>
                                    </motion.div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Selected Zones Summary */}
                    {selectedZones.size > 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="bg-sage-50 rounded-3xl p-6 border border-sage-200 shadow-sm"
                        >
                            <h4 className="font-serif font-semibold text-sage-900 mb-3">
                                Areas you've identified ({selectedZones.size}):
                            </h4>
                            <div className="space-y-2">
                                {Array.from(selectedZones.entries()).map(([zoneId, data]) => (
                                    <div
                                        key={zoneId}
                                        className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm border border-sage-100"
                                    >
                                        <span className="font-medium text-sage-800">{data.zone}</span>
                                        <span
                                            className="px-3 py-1 rounded-full text-sm font-bold text-white shadow-sm"
                                            style={{ backgroundColor: data.color }}
                                        >
                                            {data.sensation}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Pattern Detection Alert */}
                    {pattern && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-br from-earth-50 to-sage-50 rounded-3xl p-6 border-2 border-earth-300 shadow-md"
                        >
                            <div className="flex items-start gap-4">
                                <span className="text-3xl">🔍</span>
                                <div>
                                    <h4 className="font-serif font-bold text-earth-900 mb-2">
                                        Pattern Detected!
                                    </h4>
                                    <p className="text-earth-800 leading-relaxed text-sm">
                                        {pattern.message}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Continue Button */}
                    {selectedZones.size > 0 && (
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            onClick={handleContinue}
                            className="w-full bg-sage-600 text-white font-serif font-bold py-4 px-6 rounded-2xl shadow-lg hover:bg-sage-700 hover:shadow-xl transition-all transform hover:scale-[1.02]"
                        >
                            Continue to Next Step →
                        </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
}
